/**
 * Honorar-Auswertung – monatliche Übersicht der geleisteten Einsatzstunden je
 * Honorarkraft × Stundensatz = voraussichtliche Honorarsumme (zur
 * Rechnungsprüfung). Stunden stammen aus den Einsätzen im Einsatzplan.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { Loader2, Download, BarChart3 } from 'lucide-react';
import { honorarkraefteAPI, locationsAPI } from '../../lib/api';
import { toast } from 'sonner';

const MONATE = ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'];
const fmtEUR = (v) => (v == null ? '–' : new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(v));
const ABR_SHORT = { UE: 'UE (45 Min.)', Zeitstunde: 'Zeitstunde' };

export const AuswertungTab = () => {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [locFilter, setLocFilter] = useState('all');
  const [locations, setLocations] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    locationsAPI.getAll({ is_active: true }).then((r) => setLocations(r.data || [])).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await honorarkraefteAPI.auswertung(year, month, locFilter);
      setData(res.data);
    } catch {
      toast.error('Fehler beim Laden der Auswertung');
    } finally { setLoading(false); }
  }, [year, month, locFilter]);

  useEffect(() => { load(); }, [load]);

  const exportXlsx = async () => {
    setExporting(true);
    try {
      const res = await honorarkraefteAPI.auswertungExport(year, month, locFilter);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      const locName = locFilter && locFilter !== 'all'
        ? (locations.find((l) => l.id === locFilter)?.name || 'Standort').replace(/[^\wäöüÄÖÜß-]+/g, '_')
        : 'Alle-Standorte';
      a.download = `Honorar-Auswertung_${locName}_${year}-${String(month).padStart(2, '0')}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error('Export fehlgeschlagen');
    } finally { setExporting(false); }
  };

  const years = [now.getFullYear() - 1, now.getFullYear(), now.getFullYear() + 1];
  const rows = data?.rows || [];

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <Select value={String(month)} onValueChange={(v) => setMonth(Number(v))}>
          <SelectTrigger className="w-40" data-testid="auswertung-month"><SelectValue /></SelectTrigger>
          <SelectContent>{MONATE.map((m, i) => (<SelectItem key={i} value={String(i + 1)}>{m}</SelectItem>))}</SelectContent>
        </Select>
        <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
          <SelectTrigger className="w-28" data-testid="auswertung-year"><SelectValue /></SelectTrigger>
          <SelectContent>{years.map((y) => (<SelectItem key={y} value={String(y)}>{y}</SelectItem>))}</SelectContent>
        </Select>
        <Select value={locFilter} onValueChange={setLocFilter}>
          <SelectTrigger className="w-full sm:w-48" data-testid="auswertung-loc-filter"><SelectValue placeholder="Alle Standorte" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alle Standorte</SelectItem>
            {locations.map((l) => (<SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>))}
          </SelectContent>
        </Select>
        <Button variant="outline" className="rounded-full sm:ml-auto" onClick={exportXlsx} disabled={exporting || rows.length === 0} data-testid="auswertung-export-btn">
          {exporting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Download className="h-4 w-4 mr-2" />} Excel-Export
        </Button>
      </div>

      {data && (
        <div className="flex flex-wrap gap-3">
          <div className="rounded-lg border px-4 py-2">
            <div className="text-xs text-muted-foreground">Gesamtstunden (netto)</div>
            <div className="text-lg font-bold" data-testid="auswertung-total-hours">{data.gesamt_stunden} Std.</div>
          </div>
          <div className="rounded-lg border px-4 py-2">
            <div className="text-xs text-muted-foreground">Gesamt-UE (45 Min.)</div>
            <div className="text-lg font-bold" data-testid="auswertung-total-ue">{data.gesamt_ue} UE</div>
          </div>
          <div className="rounded-lg border px-4 py-2">
            <div className="text-xs text-muted-foreground">Honorar gesamt (voraussichtlich)</div>
            <div className="text-lg font-bold text-primary" data-testid="auswertung-total-sum">{fmtEUR(data.gesamt_honorar)}</div>
          </div>
        </div>
      )}

      {loading ? (
        <div className="p-8 text-center text-muted-foreground"><Loader2 className="h-6 w-6 animate-spin mx-auto" /></div>
      ) : rows.length === 0 ? (
        <div className="p-10 text-center text-muted-foreground" data-testid="auswertung-empty">
          <BarChart3 className="h-10 w-10 mx-auto mb-2 opacity-40" />
          <p>Keine Honorar-Einsätze in {MONATE[month - 1]} {year}.</p>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead className="text-right">Einsatztage</TableHead>
                <TableHead className="text-right">Stunden (netto)</TableHead>
                <TableHead className="text-right">UE (45 Min.)</TableHead>
                <TableHead>Abrechnung</TableHead>
                <TableHead className="text-right">Satz</TableHead>
                <TableHead className="text-right">Pauschale</TableHead>
                <TableHead className="text-right">Honorar gesamt</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.honorarkraft_id} data-testid={`auswertung-row-${r.honorarkraft_id}`}>
                  <TableCell className="font-medium">{r.last_name}, {r.first_name}</TableCell>
                  <TableCell className="text-right">{r.einsatz_tage}</TableCell>
                  <TableCell className="text-right">{r.stunden}</TableCell>
                  <TableCell className="text-right">{r.ue}</TableCell>
                  <TableCell><Badge variant="outline" className="text-xs">{ABR_SHORT[r.abrechnungsart] || r.abrechnungsart}</Badge></TableCell>
                  <TableCell className="text-right">{r.stundensatz != null ? `${fmtEUR(r.stundensatz)}/${r.abrechnungsart === 'UE' ? 'UE' : 'Std.'}` : '–'}</TableCell>
                  <TableCell className="text-right">{r.pauschale != null ? fmtEUR(r.pauschale) : '–'}</TableCell>
                  <TableCell className="text-right font-semibold">
                    {r.honorar_summe != null ? fmtEUR(r.honorar_summe) : <Badge variant="outline" className="text-xs">kein Satz</Badge>}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
      <p className="text-xs text-muted-foreground">
        Stunden werden aus den Einsätzen im Einsatzplan berechnet (Schichtzeiten bzw. Maßnahmen-Zeiten, Fallback 8 Std./Tag). Werte sind eine voraussichtliche Grundlage zur Rechnungsprüfung.
      </p>
    </div>
  );
};
